/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback } from 'react'
import { SpaceProps } from 'styled-system'
import { Box, Flex } from 'reflexbox'
import { useDispatch } from 'react-redux'

import { Checkbox, Icon, Typography } from '@rapidapi/ui-lib'
import { Project } from 'lib/project'
import { setProjectValue } from 'store/actions'
import { useAnyObjectProperty } from 'utils/hooks'
import { IconSymbol } from 'themes/types.d'

type CheckboxEditorProps<T extends Project.AnyObject> = SpaceProps & {
  objectRef: Project.GenericRef<T>
  objectProperty: keyof T
  label?: string
  hint?: string
  hintIcon?: IconSymbol
  disabled?: boolean
}

type CheckboxEditorReturnType = React.ReactElement<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any
> | null

const CheckboxEditor = <T extends Project.AnyObject>({
  objectRef,
  objectProperty,
  label,
  hint,
  hintIcon,
  disabled,
  ...props
}: CheckboxEditorProps<T>): CheckboxEditorReturnType => {
  // get current value
  const currentValue = useAnyObjectProperty(
    objectRef,
    objectProperty,
    undefined /* expect */,
    true /* allowsNull */,
  ) as boolean

  // dispatch update
  const dispatch = useDispatch()
  const onChange = useCallback(() => {
    dispatch(
      setProjectValue({
        objectRef,
        update: {
          [objectProperty]: !currentValue,
        },
      }),
    )
  }, [dispatch, objectRef, objectProperty, currentValue])

  return (
    <Box>
      <Flex flexDirection="row" alignItems="stretch" {...props}>
        <Checkbox
          disabled={disabled}
          checked={!!currentValue}
          onChange={onChange}
        />
        {label && (
          <Box ml={2}>
            <Typography variant="body2" mb={1} color="primary">
              {label}
            </Typography>
          </Box>
        )}
      </Flex>
      {(hintIcon || hint) && (
        <Flex alignItems="flex-start" justifyContent="flex-start" mt={1}>
          {hintIcon && (
            <Icon
              symbol={hintIcon}
              size={14}
              mr={1}
              color="content.secondary"
            />
          )}
          {hint && (
            <Box minWidth="auto">
              <Typography variant="caption" color="secondary">
                {hint}
              </Typography>
            </Box>
          )}
        </Flex>
      )}
    </Box>
  )
}

export default CheckboxEditor
